import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import DateTimePicker from 'components/DateTimePicker';
import Select from '../Select';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width || '100%'};
  max-width: ${(props) => props.maxWidth || 'none'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const HelperText = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: ${(props) => (props.error ? '#ef4444' : '#64748B')};
`;

const InputWithIcon = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const IconButton = styled.button`
  width: 42px;
  height: 42px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};
  border-radius: 6px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  color: ${(props) => (props.theme.name === 'dark' ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f8fafc'};
    border-color: #fa4616;
    color: #fa4616;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const DateSelectGrid = styled.div`
  display: grid;
  gap: 8px;
  align-items: start;
  grid-template-columns: ${({ $hasToggle }) =>
    $hasToggle ? '1.5fr 0.8fr 1fr auto' : '1.5fr 0.8fr 1fr'};

  @media (max-width: 480px) {
    grid-template-columns: ${({ $hasToggle }) =>
      $hasToggle ? '1fr 1fr auto' : '1fr 1fr'};
  }
`;

const MonthCell = styled.div`
  @media (max-width: 480px) {
    grid-column: 1 / -1;
  }
`;

const CalendarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const DropdownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Generate month options
const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Generate day options (1-31)
const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => {
  const day = String(i + 1).padStart(2, '0');
  return { value: day, label: String(i + 1) };
});

/**
 * DateSelect - A date picker using three dropdown selects for month, day, and year
 *
 * @param {string} label - Label text for the date select
 * @param {boolean} required - Whether the field is required (shows red asterisk)
 * @param {string} value - The date value in MM-DD-YYYY format
 * @param {Function} onChange - Callback when value changes, receives date string in MM-DD-YYYY format
 * @param {string} helperText - Helper/error text below the selects
 * @param {boolean} error - Whether the field has an error state
 * @param {number} startYear - First year in the year dropdown (default: current year)
 * @param {number} endYear - Last year in the year dropdown (default: current year + 5)
 * @param {string} width - Width of the container (default: "100%")
 * @param {string} maxWidth - Max width of the container
 * @param {boolean} showModeToggle - Whether to show toggle between dropdown and calendar modes
 */
const DateSelect = ({
  label,
  required = false,
  value = '',
  onChange,
  helperText,
  error = false,
  startYear,
  endYear,
  width = '100%',
  maxWidth,
  showModeToggle = false,
}) => {
  // Get current year for default
  const currentYear = useMemo(() => String(new Date().getFullYear()), []);

  // Mode toggle state: 'dropdown' or 'calendar'
  const [mode, setMode] = useState('dropdown');

  // Parse initial value from prop
  const parseValue = (val) => {
    if (!val) return { month: '', day: '', year: '' };
    const parts = val.split('-');
    if (parts.length === 3) {
      return { month: parts[0], day: parts[1], year: parts[2] };
    }
    return { month: '', day: '', year: '' };
  };

  // Use internal state to track partial selections
  // Year defaults to current year if no value provided
  const [month, setMonth] = useState(() => parseValue(value).month);
  const [day, setDay] = useState(() => parseValue(value).day);
  const [year, setYear] = useState(() => parseValue(value).year || currentYear);

  // Sync internal state when prop value changes externally
  useEffect(() => {
    const parsed = parseValue(value);
    setMonth(parsed.month);
    setDay(parsed.day);
    setYear(parsed.year || currentYear);
  }, [value, currentYear]);

  // Generate year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const start = startYear || currentYear;
    const end = endYear || currentYear + 5;
    const years = [];
    for (let y = start; y <= end; y++) {
      years.push({ value: String(y), label: String(y) });
    }
    return years;
  }, [startYear, endYear]);

  // Get valid days for selected month/year
  const validDayOptions = useMemo(() => {
    if (!month || !year) return DAY_OPTIONS;
    const daysInMonth = new Date(Number(year), Number(month), 0).getDate();
    return DAY_OPTIONS.slice(0, daysInMonth);
  }, [month, year]);

  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    // Adjust day if it exceeds days in the new month
    let adjustedDay = day;
    if (newMonth && year && day) {
      const daysInMonth = new Date(Number(year), Number(newMonth), 0).getDate();
      if (Number(day) > daysInMonth) {
        adjustedDay = String(daysInMonth).padStart(2, '0');
        setDay(adjustedDay);
      }
    }
    // Only call onChange if all fields have values
    if (newMonth && adjustedDay && year) {
      onChange(`${newMonth}-${adjustedDay}-${year}`);
    }
  };

  const handleDayChange = (newDay) => {
    setDay(newDay);
    if (month && newDay && year) {
      onChange(`${month}-${newDay}-${year}`);
    }
  };

  const handleYearChange = (newYear) => {
    setYear(newYear);
    // Adjust day if it exceeds days in the month for new year
    let adjustedDay = day;
    if (month && newYear && day) {
      const daysInMonth = new Date(Number(newYear), Number(month), 0).getDate();
      if (Number(day) > daysInMonth) {
        adjustedDay = String(daysInMonth).padStart(2, '0');
        setDay(adjustedDay);
      }
    }
    if (month && adjustedDay && newYear) {
      onChange(`${month}-${adjustedDay}-${newYear}`);
    }
  };

  // Handle calendar mode date change - convert to MM-DD-YYYY
  const handleCalendarChange = (dateValue) => {
    if (!dateValue) {
      onChange('');
      return;
    }
    // DateTimePicker may return YYYY-MM-DD string or Date object
    if (typeof dateValue === 'string') {
      const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const [, yr, mo, dy] = match;
        onChange(`${mo}-${dy}-${yr}`);
      }
    } else if (dateValue instanceof Date) {
      const mo = String(dateValue.getMonth() + 1).padStart(2, '0');
      const dy = String(dateValue.getDate()).padStart(2, '0');
      const yr = dateValue.getFullYear();
      onChange(`${mo}-${dy}-${yr}`);
    }
  };

  // Convert MM-DD-YYYY to Date object for DateTimePicker
  const calendarValue = useMemo(() => {
    if (!value) return null;
    const parts = value.split('-');
    if (parts.length === 3) {
      // parts[0] = month, parts[1] = day, parts[2] = year
      // Create date at noon to avoid timezone issues
      return new Date(
        Number(parts[2]),
        Number(parts[0]) - 1,
        Number(parts[1]),
        12,
        0,
        0
      );
    }
    return null;
  }, [value]);

  return (
    <Container width={width} maxWidth={maxWidth}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}
      {mode === 'calendar' && showModeToggle ? (
        <InputWithIcon>
          <div style={{ flex: 1 }}>
            <DateTimePicker
              value={calendarValue}
              onChange={handleCalendarChange}
              showTime={false}
            />
          </div>
          <IconButton
            type="button"
            onClick={() => setMode('dropdown')}
            title="Switch to dropdowns"
          >
            <DropdownIcon />
          </IconButton>
        </InputWithIcon>
      ) : showModeToggle ? (
        <DateSelectGrid $hasToggle>
          <MonthCell>
            <Select
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              placeholder="Month"
              options={MONTH_OPTIONS}
              error={error}
            />
          </MonthCell>
          <div>
            <Select
              value={day}
              onChange={(e) => handleDayChange(e.target.value)}
              placeholder="Day"
              options={validDayOptions}
              error={error}
            />
          </div>
          <div>
            <Select
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
              placeholder="Year"
              options={yearOptions}
              error={error}
            />
          </div>
          <IconButton
            type="button"
            onClick={() => setMode('calendar')}
            title="Switch to calendar"
          >
            <CalendarIcon />
          </IconButton>
        </DateSelectGrid>
      ) : (
        <DateSelectGrid>
          <MonthCell>
            <Select
              value={month}
              onChange={(e) => handleMonthChange(e.target.value)}
              placeholder="Month"
              options={MONTH_OPTIONS}
              error={error}
            />
          </MonthCell>
          <div>
            <Select
              value={day}
              onChange={(e) => handleDayChange(e.target.value)}
              placeholder="Day"
              options={validDayOptions}
              error={error}
            />
          </div>
          <div>
            <Select
              value={year}
              onChange={(e) => handleYearChange(e.target.value)}
              placeholder="Year"
              options={yearOptions}
              error={error}
            />
          </div>
        </DateSelectGrid>
      )}
      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </Container>
  );
};

DateSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  startYear: PropTypes.number,
  endYear: PropTypes.number,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  showModeToggle: PropTypes.bool,
};

export default DateSelect;
